import {
  Activity,
  BadgeCheck,
  CalendarRange,
  CircleDollarSign,
  ClipboardList,
  Headphones,
  LayoutDashboard,
  LineChart,
  MoreHorizontal,
  MessageSquare,
  Receipt,
  ShieldCheck,
  ShoppingBag,
  Trophy,
  UsersRound,
  Megaphone,
  Map,
  type LucideIcon,
} from 'lucide-react';
import type { CommandCenterKind } from '../permissions/permissions';

/**
 * Single source of truth that maps each command-center "space" to its UI
 * metadata: route, label, icon, marketing copy, and the navigation group it
 * belongs to. Sidebar, bottom-nav, topbar title and dashboards all derive
 * their content from this list, which is how the app feels seamless.
 */
export interface SpaceConfig {
  kind: CommandCenterKind;
  path: string;
  label: string;
  short: string;
  group: 'operate' | 'compete' | 'experience' | 'extras';
  icon: LucideIcon;
  blurb: string;
  accent: 'arc' | 'ice' | 'goal';
}

export const SPACES: SpaceConfig[] = [
  {
    kind: 'admin',
    path: '/admin',
    label: 'Admin Command Center',
    short: 'Admin',
    group: 'operate',
    icon: LayoutDashboard,
    blurb: 'Run the tournament. Schedule, assign, monitor, communicate.',
    accent: 'arc',
  },
  {
    kind: 'tournaments',
    path: '/tournaments',
    label: 'Tournaments',
    short: 'Tournaments',
    group: 'operate',
    icon: Trophy,
    blurb: 'Create, configure and publish tournaments.',
    accent: 'arc',
  },
  {
    kind: 'scheduler',
    path: '/scheduler',
    label: 'Smart Scheduler',
    short: 'Scheduler',
    group: 'operate',
    icon: CalendarRange,
    blurb: 'Generate and refine the tournament schedule with conflict detection.',
    accent: 'arc',
  },
  {
    kind: 'operations',
    path: '/operations',
    label: 'Operations Assignments',
    short: 'Operations',
    group: 'operate',
    icon: ClipboardList,
    blurb: 'Assign scorekeepers and refs to every game with conflict detection.',
    accent: 'arc',
  },
  {
    kind: 'directory',
    path: '/directory',
    label: 'Directory & Roles',
    short: 'Directory',
    group: 'operate',
    icon: UsersRound,
    blurb: 'People, profiles, role assignments. The roster of your event.',
    accent: 'arc',
  },
  {
    kind: 'communication_hub',
    path: '/communication-hub',
    label: 'Communication Hub',
    short: 'Comms',
    group: 'operate',
    icon: Megaphone,
    blurb: 'Manual segmented messages — never auto-blasts.',
    accent: 'arc',
  },
  {
    kind: 'scorekeeper',
    path: '/scorekeeper',
    label: 'Scorekeeper Command Center',
    short: 'Scorekeeper',
    group: 'compete',
    icon: ClipboardList,
    blurb: 'Your assigned games, rosters and quick scoring controls.',
    accent: 'goal',
  },
  {
    kind: 'captain',
    path: '/captain',
    label: 'Captain Command Center',
    short: 'Captain',
    group: 'compete',
    icon: ShieldCheck,
    blurb: 'Confirm the roster, prep the team, talk to your players.',
    accent: 'ice',
  },
  {
    kind: 'player',
    path: '/player',
    label: 'Player Dashboard',
    short: 'Player',
    group: 'compete',
    icon: BadgeCheck,
    blurb: 'Your profile, stats, schedule and tournament history.',
    accent: 'ice',
  },
  {
    kind: 'stands',
    path: '/stands',
    label: 'Stands',
    short: 'Stands',
    group: 'experience',
    icon: Trophy,
    blurb: 'Live scores, standings, brackets, hot players.',
    accent: 'goal',
  },
  {
    kind: 'locker_room',
    path: '/locker-room',
    label: 'Locker Room',
    short: 'Locker Room',
    group: 'experience',
    icon: MessageSquare,
    blurb: 'Private team space — chat, notes, pre-game prep.',
    accent: 'ice',
  },
  {
    kind: 'front_desk',
    path: '/front-desk',
    label: 'Front Desk',
    short: 'Front Desk',
    group: 'experience',
    icon: Headphones,
    blurb: 'The information booth: who, what, where, when.',
    accent: 'arc',
  },
  {
    kind: 'pro_shop',
    path: '/pro-shop',
    label: 'Pro Shop',
    short: 'Pro Shop',
    group: 'extras',
    icon: ShoppingBag,
    blurb: 'Optional tournament merch.',
    accent: 'goal',
  },
  {
    kind: 'concessions',
    path: '/concessions',
    label: 'Concessions',
    short: 'Concessions',
    group: 'extras',
    icon: CircleDollarSign,
    blurb: 'Optional food and drink ordering.',
    accent: 'goal',
  },
];

export const SPACE_BY_KIND: Record<CommandCenterKind, SpaceConfig> = Object.fromEntries(
  SPACES.map((s) => [s.kind, s]),
) as Record<CommandCenterKind, SpaceConfig>;

export const GROUP_LABEL: Record<SpaceConfig['group'], string> = {
  operate: 'Operate',
  compete: 'Compete',
  experience: 'Experience',
  extras: 'Extras',
};

// Re-export so consumers can render decorative icons without importing lucide twice.
export { Activity, Map };

/**
 * Admin sidebar anchors — the League HQ navigation, organized by
 * lifecycle. Two groups: "Run the league" (day-to-day operations) and
 * "Grow the league" (analytics, settings). Each anchor matches a top-level
 * route under `/admin/*`.
 */
export interface AdminSidebarAnchor {
  path: string;
  label: string;
  /** Purpose copy for the top module container when the topbar already owns the page name. */
  heroPurpose: string;
  icon: LucideIcon;
  group: 'run' | 'grow';
  /** When true, only highlight on exact-match (Home shouldn't match every prefix). */
  end?: boolean;
}

export interface AdminMobileNavAnchor {
  path: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  hash?: string;
}

export const ADMIN_SIDEBAR_ANCHORS: AdminSidebarAnchor[] = [
  {
    path: '/admin',
    label: 'Dashboard',
    heroPurpose: 'Review setup, events, and league operations from one action center.',
    icon: LayoutDashboard,
    group: 'run',
    end: true,
  },
  {
    path: '/admin/comms',
    label: 'Comms',
    heroPurpose: 'Draft, send, and audit communications using Directory audiences.',
    icon: Megaphone,
    group: 'run',
  },
  {
    path: '/admin/directory',
    label: 'Directory',
    heroPurpose: 'Manage every person connected to the league from one source of truth.',
    icon: UsersRound,
    group: 'run',
  },
  {
    path: '/admin/seasons',
    label: 'Seasons',
    heroPurpose: 'Run recurring seasons from week one to playoffs.',
    icon: CalendarRange,
    group: 'run',
  },
  {
    path: '/admin/tournaments',
    label: 'Tournaments',
    heroPurpose: 'Run tournament drafts, setup, publishing, and event workspaces.',
    icon: Trophy,
    group: 'run',
  },
  {
    path: '/admin/insights',
    label: 'Insights',
    heroPurpose: 'Insights will be built from registrations, schedules, payments, and completed games.',
    icon: LineChart,
    group: 'grow',
  },
  {
    path: '/admin/billing',
    label: 'Financials',
    heroPurpose: 'Track subscriptions, invoices, registration revenue, payouts, and negotiated terms.',
    icon: Receipt,
    group: 'grow',
  },
  {
    path: '/admin/settings',
    label: 'Settings',
    heroPurpose: 'Configure league-wide defaults, public presentation, communications, and login history.',
    icon: ShieldCheck,
    group: 'grow',
  },
];

export const ADMIN_MOBILE_PRIMARY_ANCHORS: AdminMobileNavAnchor[] = [
  { path: '/admin', label: 'Home', icon: LayoutDashboard, end: true },
  { path: '/admin/tournaments', label: 'Events', icon: Trophy },
  { path: '/admin/comms', label: 'Comms', icon: Megaphone },
  { path: '/admin/directory', label: 'Directory', icon: UsersRound },
];

export const ADMIN_MOBILE_MORE_ANCHORS: AdminMobileNavAnchor[] = [
  { path: '/admin/seasons', label: 'Seasons', icon: CalendarRange },
  { path: '/admin/insights', label: 'Insights', icon: LineChart },
  { path: '/admin/billing', label: 'Financials', icon: Receipt },
  { path: '/admin/settings', label: 'Settings', icon: ShieldCheck },
  { path: '/events', label: 'Public site', icon: MoreHorizontal },
];

export const ADMIN_SIDEBAR_GROUPS: Record<'run' | 'grow', string> = {
  run: 'Run the league',
  grow: 'Grow the league',
};
